import moment from 'moment/moment';
import { useMemo } from 'react';

import { EmployeeCalendarPersonWithLegalEntityId } from '~/components/EmployeeCalendar';
import {
  UseEmployeeCalendarPublicHolidaysProps,
  useEmployeeCalendarPublicHolidays,
} from '~/components/EmployeeCalendar/Absences';
import { AbsenceEntry, AbsenceType } from '~/data/absences/types';
import { SelectedEmployeeData } from '~/hooks/employee/useGetSelectedEmployeeData';
import { convertAbsenceEntriesToEmployeeCalendarEvents } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesCalendar/data';

export interface AbsencesCalendarProps {
  employeeId: number;
  absenceEntries: AbsenceEntry[];
  containerWidth: number;
  shouldRedirect: boolean;
  employeeData?: SelectedEmployeeData;
  accessMode: 'full' | 'limited';
}

export const useAbsenceCalendarCommon = (props: AbsencesCalendarProps) => {
  const { absenceEntries, employeeId, containerWidth, employeeData } = props;

  const { range, publicHolidaysProps, calendarPerson } = useMemo(() => {
    const range = {
      start: moment().startOf('day').toDate(),
      end: moment().add(90, 'days').startOf('day').toDate(),
    };

    const calendarPerson: EmployeeCalendarPersonWithLegalEntityId = {
      employeeId,
      firstName: employeeData?.firstName ?? '',
      lastName: employeeData?.lastName ?? '',
      legalEntityId: 0,
      profilePic: employeeData?.profilePictureId,
    };

    const publicHolidaysProps: UseEmployeeCalendarPublicHolidaysProps = {
      startDate: range.start,
      endDate: range.end,
      reportEmployees: [calendarPerson],
    };

    return {
      range,
      calendarPerson,
      publicHolidaysProps,
    };
  }, [employeeId, employeeData]);

  const { publicHolidayEvents, isLoading: isLoadingPublicHolidays } =
    useEmployeeCalendarPublicHolidays(publicHolidaysProps);

  const { allEvents } = useMemo(() => {
    const absenceEntriesInRange = absenceEntries.filter((entry) => {
      const { startDate, endDate, type } = entry;

      const isDateInRange = (date?: Date, start?: Date, end?: Date) =>
        !!date && !!start && !!end
          ? moment(date).isBetween(moment(start), moment(end), 'day', '[]')
          : false;

      const eventInRange = (
        eventStart?: Date,
        eventEnd?: Date,
        rangeStart?: Date,
        rangeEnd?: Date,
      ) => {
        const eventStartOrEndIsInRange =
          isDateInRange(eventStart, rangeStart, rangeEnd) ||
          isDateInRange(eventEnd, rangeStart, rangeEnd);

        const rangeIsWithinEvent =
          isDateInRange(rangeStart, eventStart, eventEnd) ||
          isDateInRange(rangeEnd, eventStart, eventEnd);

        return eventStartOrEndIsInRange || rangeIsWithinEvent;
      };

      switch (type) {
        case AbsenceType.SHORT: {
          return eventInRange(startDate, endDate, range.start, range.end);
        }
        case AbsenceType.LONG: {
          return eventInRange(
            startDate,
            endDate ?? moment().endOf('day').toDate(),
            range.start,
            range.end,
          );
        }
        default:
          return false;
      }
    });

    const absenceEvents = convertAbsenceEntriesToEmployeeCalendarEvents(
      absenceEntriesInRange,
      calendarPerson,
    );
    const allEvents = publicHolidayEvents.concat(absenceEvents);

    return {
      allEvents,
    };
  }, [absenceEntries, publicHolidayEvents, range, calendarPerson]);

  return {
    containerWidth,
    range,
    calendarPerson,
    allEvents,
    isLoading: isLoadingPublicHolidays,
  };
};
